// Public contact form — route /#/contact. Submits to the existing backend endpoint
// POST /users/contactMail (unchanged). Field shape matches the legacy client:
// { name, email, message, check_privacy, check_promotion }.
import { useState } from 'react'
import StaticPageLayout from './StaticPageLayout'
import { httpRequest } from '../../core/http'
import { t } from '../../i18n'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const Contact = () => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [checkPrivacy, setCheckPrivacy] = useState(false)
  const [checkPromotion, setCheckPromotion] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !message.trim()) {
      setError(t.fieldRequired)
      return
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError(t.invalidEmail)
      return
    }
    if (!checkPrivacy) {
      setError(t.contactPrivacyRequired)
      return
    }
    setError('')
    setIsSubmitting(true)

    const data = await httpRequest('users/contactMail', 'POST', {
      name: name.trim(),
      email: email.trim(),
      message: message.trim(),
      check_privacy: checkPrivacy,
      check_promotion: checkPromotion,
    })

    setIsSubmitting(false)
    if (data && data.status === 'success') {
      setResult({ ok: true, message: data.message || t.contactSuccess })
    } else {
      setResult({ ok: false, message: (data && data.message) || t.contactError })
    }
  }

  if (result) {
    return (
      <StaticPageLayout title={t.contactTitle}>
        <p className="cf-subtitle">{result.message}</p>
        {!result.ok && (
          <button type="button" className="cf-btn cf-btn-secondary" onClick={() => setResult(null)}>
            {t.retry}
          </button>
        )}
      </StaticPageLayout>
    )
  }

  return (
    <StaticPageLayout title={t.contactTitle}>
      <p className="cf-subtitle">{t.contactIntro}</p>
      <form onSubmit={handleSubmit} autoComplete="off" noValidate>
        <div className="cf-field">
          <label htmlFor="cf-name">{t.contactNameLabel}</label>
          <input id="cf-name" type="text" maxLength={25} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="cf-field">
          <label htmlFor="cf-email">{t.emailLabel}</label>
          <input id="cf-email" type="email" maxLength={50} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="cf-field">
          <label htmlFor="cf-message">{t.contactMessageLabel}</label>
          <textarea id="cf-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />
        </div>

        <label className="cf-check">
          <input type="checkbox" checked={checkPrivacy} onChange={(e) => setCheckPrivacy(e.target.checked)} />
          <span>
            {t.contactPrivacyConsent}{' '}
            <a href="#/privacy-policy">{t.privacyPolicyTitle}</a>
          </span>
        </label>
        <label className="cf-check">
          <input type="checkbox" checked={checkPromotion} onChange={(e) => setCheckPromotion(e.target.checked)} />
          <span>{t.contactPromotionConsent}</span>
        </label>

        {error && <span className="cf-error">{error}</span>}
        <button type="submit" className="cf-btn cf-btn-primary" disabled={isSubmitting}>
          {isSubmitting ? t.contactSending : t.contactSubmit}
        </button>
      </form>
    </StaticPageLayout>
  )
}

export default Contact
